export type InventoryItemKind = 'FINISHED' | 'PART' | 'MATERIAL';

/** 사용중 / 단종 */
export type InventoryItemStatus = 'ACTIVE' | 'DISCONTINUED';

export interface CompanyInventoryItem {
  inventoryId: number;
  masterId?: number | null;
  productId?: number | null;
  itemKind: InventoryItemKind | string;
  itemCode?: string | null;
  itemName: string;
  specText?: string | null;
  makerName?: string | null;
  location?: string | null;
  stockQty?: number | string | null;
  minStockQty?: number | string | null;
  unit?: string | null;
  itemStatus?: InventoryItemStatus | string | null;
  remark?: string | null;
  lowStock?: boolean | null;
  createdAt?: string | null;
  updatedAt?: string | null;
}

export interface CompanyInventoryMaster {
  masterId: number;
  itemKind: InventoryItemKind | string;
  itemName: string;
  defaultUnit?: string | null;
  makerName?: string | null;
  itemStatus?: InventoryItemStatus | string | null;
  remark?: string | null;
  specCount?: number | null;
  lowStock?: boolean | null;
  createdAt?: string | null;
  updatedAt?: string | null;
  items?: CompanyInventoryItem[];
}

export interface CompanyInventoryMasterPayload {
  itemKind: InventoryItemKind | string;
  itemName: string;
  defaultUnit?: string;
  makerName?: string;
  itemStatus?: InventoryItemStatus;
  remark?: string;
}

export interface CompanyInventoryPayload {
  masterId?: number | null;
  itemKind: InventoryItemKind | string;
  itemCode?: string;
  itemName: string;
  specText?: string;
  makerName?: string;
  location?: string;
  stockQty?: number | null;
  minStockQty?: number | null;
  unit?: string;
  itemStatus?: InventoryItemStatus;
  remark?: string;
  /** 재고 수량 변경 시 사유 */
  stockAdjustReason?: string;
}

export type InventoryMovementType =
  | 'IN_ACCEPTANCE'
  | 'IN_FINAL'
  | 'OUT_MID'
  | 'OUT_NCR'
  | 'ADJUST'
  | string;

export interface CompanyInventoryMovement {
  movementId: number;
  inventoryId: number;
  qty: number | string;
  balanceAfter?: number | string | null;
  movementType: InventoryMovementType;
  movementTypeLabel?: string | null;
  refType?: string | null;
  refId?: number | null;
  lotNo?: string | null;
  remark?: string | null;
  occurredAt?: string | null;
}

export function inventoryMovementTypeLabel(type?: string | null, fallback?: string | null) {
  if (fallback) return fallback;
  switch (type) {
    case 'IN_ACCEPTANCE':
      return '인수검사 입고';
    case 'IN_FINAL':
      return '제품검사 입고';
    case 'OUT_MID':
      return '중간검사 출고';
    case 'OUT_NCR':
      return '부적합 출고';
    case 'ADJUST':
      return '수동 조정';
    default:
      return type || '-';
  }
}

export function formatInventoryMovementQty(value?: number | string | null) {
  if (value == null || value === '') return '0';
  const n = Number(value);
  if (Number.isNaN(n)) return String(value);
  if (n > 0) return `+${String(value)}`;
  return String(value);
}

export const INVENTORY_UNIT_PRESETS = ['EA', '개', 'kg', 'g', 'm', 'mm', 'L', '식', 'SET', 'LOT'] as const;

export function inventoryKindLabel(kind?: string | null) {
  switch (kind) {
    case 'FINISHED':
      return '완제품';
    case 'PART':
      return '부품';
    case 'MATERIAL':
      return '원자재';
    default:
      return kind || '-';
  }
}

export function inventoryItemStatusLabel(status?: string | null) {
  return status === 'DISCONTINUED' ? '단종' : '사용중';
}

export function formatInventoryQty(value?: number | string | null) {
  if (value == null || value === '') return '0';
  const cleaned = String(value).replace(/,/g, '');
  const n = Number(cleaned);
  if (!Number.isFinite(n)) return String(value);
  return n.toLocaleString('ko-KR');
}

export function isInventoryLowStock(row: Pick<CompanyInventoryItem, 'stockQty' | 'minStockQty' | 'lowStock'>) {
  if (row.lowStock === true) return true;
  const stock = Number(row.stockQty ?? 0);
  const min = Number(row.minStockQty ?? 0);
  return !Number.isNaN(stock) && !Number.isNaN(min) && min > 0 && stock < min;
}
